/* eslint-disable react/prop-types */
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAuthorization from '../../app/hooks/useAuthorization'
import * as builder from '../ducks/builder'
import { useGetFeatureFlagsQuery } from '../services/featureFlags'
import { displayPage, resetDefaultPermission } from '../utils/utils'
import { LayoutSplashScreen } from './LayoutContext'

/**
 * Used to synchronize current layout `menuConfig`, `layoutConfig` and
 * `htmlClassService` with redux store.
 */
export default function LayoutInitializer({ children, menuConfig, layoutConfig, htmlClassService }) {
  const dispatch = useDispatch()
  const builderState = useSelector(({ builder }) => builder) // eslint-disable-line no-shadow
  const { data: featureFlagsData = {} } = useGetFeatureFlagsQuery()
  const permissions = useSelector(({ userPermissions }) => userPermissions.data.permissions)

  const hasValidM365LicensingReportSubscription = useSelector(
    ({ meta }) => meta.hasValidM365LicensingReportSubscription,
  )
  const hasValidPostureBaselineSubscription = useSelector(({ meta }) => meta.hasValidPostureBaselineSubscription)
  const hasMfaOnlySubscription = useSelector(({ meta }) => meta.hasMfaOnlySubscription)

  const { isAuthorizedForDiscover, isAuthorizedForGroups } = useAuthorization()

  useEffect(() => {
    if (Object.keys(featureFlagsData).length > 0 && menuConfig) {
      const isPostureProtectM3Enabled = featureFlagsData['posture-protect-m3']
      const isDiscoverComplianceEnabled = featureFlagsData['discover-security']
      const isEngageAutopilotEnabled = featureFlagsData['engage-autopilot']
      const isAugmenttAiEnabled = featureFlagsData['augmentt-ai-assistant']
      const isDefenderPoliciesEnabled = featureFlagsData['defender-policies']
      const isComplianceAuditEnabled = featureFlagsData['secure-compliance-audit']

      // reset default permission
      resetDefaultPermission(menuConfig)
      // Retrieve all feature flag menu items (features whose name includes 'menu')
      const menuFeatureFlags = Object.entries(featureFlagsData)
        .filter(([feature]) => feature.includes('menu'))
        .map(([feature, enabled]) => ({ feature, enabled }))

      // Check feature flags, modify the menu config here based on them
      // eslint-disable-next-line no-restricted-syntax
      for (const menuFeatureFlag of menuFeatureFlags) {
        let menuEnabled = false

        if (menuFeatureFlag?.enabled) {
          menuEnabled = true
        }

        try {
          // Secure
          if (menuFeatureFlag.feature.includes('secure')) {
            const secureMenuItem = menuConfig.aside.items.find((item) => item.page === 'secure') // Secure Menu
            secureMenuItem.visible = menuEnabled // Main item

            for (let i = 0; i < secureMenuItem.submenu.length; i += 1) {
              if (hasMfaOnlySubscription) {
                if (secureMenuItem.submenu[i].page === 'secure/security-posture-menu') {
                  secureMenuItem.submenu[i].preview = false
                  const postureTemplateItem = secureMenuItem.submenu[i].submenu.find(
                    (item) => item.page === 'secure/posture-templates',
                  )
                  postureTemplateItem.preview = true
                } else {
                  secureMenuItem.submenu[i].preview = true
                }
              }

              // Secure Submenus
              secureMenuItem.submenu[i].visible = menuEnabled

              // disable posture-templates based on ff
              if (!isPostureProtectM3Enabled && secureMenuItem.submenu[i].page === 'secure/posture-templates') {
                secureMenuItem.submenu[i].visible = false
              }

              // If posture baseline subscription not set, set as preview only
              if (
                !hasValidPostureBaselineSubscription &&
                secureMenuItem.submenu[i].page === 'secure/posture-templates'
              ) {
                secureMenuItem.submenu[i].preview = true
              }

              if (secureMenuItem.submenu[i].page === 'secure/posture-templates/:templateId') {
                secureMenuItem.submenu[i].visible = false // always false, used just for the title of the subpage
              }

              if (secureMenuItem.submenu[i].page === 'secure/security-posture-menu') {
                const complianceAuditItem = secureMenuItem.submenu[i].submenu.find(
                  (item) => item.page === 'secure/compliance-audit',
                )

                if (complianceAuditItem) {
                  complianceAuditItem.visible = isComplianceAuditEnabled
                }
              }

              if (
                !isDefenderPoliciesEnabled &&
                secureMenuItem.submenu[i].page === 'secure/policy-management/policies'
              ) {
                const defenderMenu = secureMenuItem.submenu[i].submenu.find(
                  (menu) => menu.page === 'secure/defender/policies',
                )
                defenderMenu.visible = false
              }

              if (
                !hasValidPostureBaselineSubscription &&
                (secureMenuItem.submenu[i].page === 'secure/policy-templates' ||
                  secureMenuItem.submenu[i].page === 'secure/policy-management' ||
                  secureMenuItem.submenu[i].page === 'secure/policy-management/policies' ||
                  secureMenuItem.submenu[i].page === 'secure/policy-management/named-locations' ||
                  secureMenuItem.submenu[i].page === 'secure/defender' ||
                  secureMenuItem.submenu[i].page === 'secure/defender/policies' ||
                  secureMenuItem.submenu[i].page === 'secure/defender/templates' ||
                  secureMenuItem.submenu[i].page === 'secure/entra-id')
              ) {
                // When licensing is not valid the parent menu item and submenu will be greyed out
                // and the submenu will be disabled. Set preview on the parent menu item to style as grey
                // without disabling to keep the submenu expandable, while the submenu items are disabled.
                secureMenuItem.submenu[i].preview = true

                // eslint-disable-next-line no-plusplus
                for (let x = 0; x < secureMenuItem.submenu[i].submenu.length; x++) {
                  secureMenuItem.submenu[i].submenu[x].disabled = true
                }
              }

              if (secureMenuItem.submenu[i].page === 'secure/entra-id') {
                const groupsSubmenu = secureMenuItem.submenu[i]

                if (!isAuthorizedForGroups && hasValidPostureBaselineSubscription) {
                  groupsSubmenu.visible = false
                }
              }
            }
          } else if (menuFeatureFlag.feature.includes('alerts')) {
            // Alerts
            const configurationMenu = menuConfig?.aside?.items.find((obj) => obj.page === 'system-configuration')

            const alertsMenuItem = configurationMenu?.submenu.find((obj) => obj.page === 'alerts')

            if (alertsMenuItem) {
              alertsMenuItem.visible = menuEnabled
            }
          }
        } catch (e) {
          console.error('ERROR, secure menu error', e)
        }

        try {
          const reportsMenuItem = menuConfig.aside.items.find((item) => item.page === 'reports') // reports menu

          if (hasMfaOnlySubscription) {
            const mfaOnlyAllowedPages = ['reports/threats', 'reports/mfa', 'reports/licensing', 'scheduled-reports']
            reportsMenuItem?.submenu?.forEach((item) => {
              if (!mfaOnlyAllowedPages.includes(item.page)) {
                item.preview = true // eslint-disable-line no-param-reassign
              }
            })
          }

          // License report - menu item visible or not
          const licenseReportMenuItem = reportsMenuItem?.submenu?.find((item) => item.page === 'reports/licensing')

          // Check if the feature flag is enabled, also check if the organization has the relevant subscription
          if (
            licenseReportMenuItem &&
            menuFeatureFlag.feature.includes('license') &&
            hasValidM365LicensingReportSubscription != null
          ) {
            licenseReportMenuItem.visible = menuEnabled
            // Check if the org has subscription, that overrides the FF with its state
            if (licenseReportMenuItem.visible && !hasValidM365LicensingReportSubscription) {
              // License Report Preview
              const licenseReportPreviewMenuItem = menuConfig.aside.items.find(
                (item) => item.page === 'reports/licensing-preview',
              )
              licenseReportMenuItem.visible = false
              licenseReportPreviewMenuItem.visible = true
            }
          }
        } catch (e) {
          console.error('ERROR, reports/licensing menu error', e)
        }

        try {
          // Development page should only appear while FF enabled
          const configMenuItem = menuConfig.aside.items.find((item) => item.page === 'system-configuration')

          if (configMenuItem && configMenuItem?.submenu?.length > 0) {
            const developmentPage = configMenuItem?.submenu?.find((sub) => sub.page === 'development')

            if (developmentPage) {
              if (isAugmenttAiEnabled) {
                developmentPage.visible = true
              } else {
                developmentPage.visible = false
              }
            }
          }
        } catch (e) {
          console.error('Error', e)
        }

        try {
          // Discover - menu item visible or not
          const discoverMenuItem = menuConfig.aside.items.find((item) => item.page === 'discovery') // Discover menu

          if (!isAuthorizedForDiscover) {
            discoverMenuItem.visible = false
          }

          if (hasMfaOnlySubscription) {
            discoverMenuItem.preview = true
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < discoverMenuItem.submenu.length; i++) {
              discoverMenuItem.submenu[i].preview = true
            }
          }

          const discoverComplianceMenuItem = discoverMenuItem?.submenu.find(
            (subItem) => subItem.page === 'discover/risk',
          )
          if (discoverComplianceMenuItem && !isDiscoverComplianceEnabled) {
            discoverComplianceMenuItem.visible = false
          }
        } catch (e) {
          console.error('ERROR, Discover-report menu error', e)
        }

        try {
          // Engage
          const engageMenuItem = menuConfig.aside.items.find((item) => item.page === 'engage')
          if (hasMfaOnlySubscription) {
            engageMenuItem.preview = true
          }

          const engageActionLog = engageMenuItem.submenu.find((item) => item.page === 'engage/action-log')
          engageActionLog.visible = isEngageAutopilotEnabled

          const distributionListsMenuItem = engageMenuItem.submenu.find(
            (item) => item.page === 'engage/distribution-lists',
          )
          if (distributionListsMenuItem) {
            distributionListsMenuItem.visible = isEngageAutopilotEnabled
          }
        } catch (e) {
          console.error('ERROR, Engage menu error', e)
        }
      }

      // Update menu config
      dispatch(builder.actions.setMenuConfig(menuConfig))
    }

    if (permissions && menuConfig) {
      // biome-ignore lint/complexity/noForEach: <explanation>
      menuConfig.aside.items.forEach((menuItem) => {
        if (!displayPage(`/${menuItem.page}`, permissions)) {
          menuItem.visible = false // eslint-disable-line no-param-reassign
        } else if (menuItem.submenu) {
          // biome-ignore lint/complexity/noForEach: <explanation>
          menuItem.submenu.forEach((submenuItem) => {
            if (!displayPage(`/${submenuItem.page}`, permissions)) {
              submenuItem.visible = false // eslint-disable-line no-param-reassign
            } else if (submenuItem.submenu) {
              // biome-ignore lint/complexity/noForEach: <explanation>
              submenuItem.submenu.forEach((submenuItem2) => {
                if (!displayPage(`/${submenuItem2.page}`, permissions)) {
                  submenuItem2.visible = false // eslint-disable-line no-param-reassign
                } else if (submenuItem2.submenu) {
                  submenuItem2.submenu.forEach((submenuItem3) => {
                    if (!displayPage(`/${submenuItem3.page}`, permissions)) {
                      submenuItem3.visible = false // eslint-disable-line no-param-reassign
                    }
                  })
                }
              })
            }
          })

          // if all submenus under a menu are hidden, hide the parent menu
          if (menuItem.submenu.every((submenuItem) => submenuItem.visible !== true)) {
            menuItem.visible = false // eslint-disable-line no-param-reassign
          }
        }
      })

      // hide configuration parent menu if no child found
      const configMenu = menuConfig.aside.items.find((x) => x.page === 'system-configuration')
      if (configMenu) {
        let configMenuChildrenVisible = false
        // eslint-disable-next-line array-callback-return
        configMenu.submenu.map((sub) => {
          if (sub.visible) {
            configMenuChildrenVisible = true
          }
        })
        configMenu.visible = configMenuChildrenVisible
      }
    }
  }, [
    menuConfig,
    featureFlagsData,
    permissions,
    dispatch,
    hasValidM365LicensingReportSubscription,
    hasValidPostureBaselineSubscription,
    hasMfaOnlySubscription,
    isAuthorizedForDiscover,
    isAuthorizedForGroups,
  ])

  useEffect(() => {
    if (layoutConfig.demo !== builderState.layoutConfig.demo) {
      dispatch(builder.actions.setLayoutConfigs(layoutConfig))
    }
  }, [dispatch, builderState, layoutConfig])

  useEffect(() => {
    dispatch(builder.actions.setHtmlClassService(htmlClassService))
  }, [dispatch, htmlClassService])

  return htmlClassService === builderState.htmlClassServiceObjects ? (
    // Render content when `htmlClassService` synchronized with redux store.
    children
  ) : (
    // Otherwise sow loading splash screen.
    <LayoutSplashScreen />
  )
}
